import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Search, Trash2, Share2, Copy, FileText, X, Check, PackageSearch, Wrench, Pencil,
} from 'lucide-react';
import { contar } from '@fiodecobre/shared';
import { FotoProduto } from '../../components/FotoProduto';
import {
  appApi, ErroApp,
  type OrcamentoApp, type ResumoOrcamentoApp, type ProdutoBusca, type CorpoSalvarOrcamento,
} from './api';
import { estiloCampo } from './campos';
import { gerarPdfDoPrestador } from './orcamento-pdf';

// AppCel — "Orcamentos": o documento que o PRESTADOR entrega ao cliente DELE.
//
// ⚠️ **Na tela ela se chama "Orcamentos" desde 18/08/2026** ([DA-575]) — decisao
// dele ao ver a home nova. O nome antigo, "Meus Orcamentos", segue nos comentarios
// deste modulo, nos ADRs ([DA-565], [DA-566]) e nas migrations 0236/0237, que sao
// registro datado e nao se reescrevem. Sao a MESMA area.
//
// ⚠️ Eu recomendei manter "Meus", com o argumento de que "Orcamentos" sozinho, na
// home, le como *"os orcamentos que a LOJA me mandou"* — coisa que nao existe. Ele
// decidiu pelo curto. Fica registrado porque, se um dia aparecer confusao de
// cliente perguntando por orcamento da loja, o rotulo e' o primeiro suspeito.
// Fase 1, 17/08/2026 (decisao do Ruan).
//
// **O que decide a adocao desta tela e' a VELOCIDADE de montagem.** Se montar aqui
// for mais lento do que digitar cinco itens no WhatsApp, ele nao volta. Cada escolha
// abaixo sai disso:
//
//  - o documento **nasce no primeiro toque** e **salva sozinho** — nao ha botao
//    "criar" nem botao "salvar" no caminho;
//  - a busca de material **fica aberta** enquanto ele adiciona: a lista de compras
//    dele tem varios itens seguidos, e fechar a folha a cada um dobraria os toques;
//  - **"Salvar como"** duplica qualquer orcamento pronto — e' a peca CENTRAL da
//    fase, nao um extra. Quem instala chuveiro monta quase o mesmo papel toda semana.
//
// ⚠️ **MODELO GUARDA ITEM, NUNCA PRECO.** Ao sair de um modelo, o preco do material
// e' repuxado do catalogo vivo (o backend faz isso ao duplicar). Modelo com preco
// congelado de tres meses atras entregaria valor errado ao cliente dele.
//
// ⛔ **Nao esta aqui, de proposito**: "enviar pra loja" (fase 2), markup, credito,
// selo de Parceiro, venda, nota, estoque ou comissao. A ferramenta abre pra QUALQUER
// conta do AppCel — o portao ja e' o convite ([DA-558]).

const COBRE = '#b45309';
const CINZA = '#6b7280';
const BORDA = '#e7e5e4';

/**
 * Descricao de item vai em CAIXA ALTA, igual ao ERP (pedido dele, 20/08/2026).
 *
 * ⚠️ **E' exibicao, nao dado** — o texto continua gravado como esta na fonte. Duas
 * fontes alimentam esta lista e so uma e' maiuscula: a `descricao` do produto (a que
 * a NF e o caixa usam) vem sempre em caixa alta, mas o AppCel prefere a
 * `descricao_comercial`, escrita pra vitrine em Caixa De Titulo — sao **132 produtos
 * de 1.411** com comercial, medidos em 20/08. Levantar aqui iguala as duas sem tocar
 * na vitrine, onde o Title Case e' proposital.
 *
 * ⛔ **Uma peca so pros dois lugares** (a lista do orcamento e o resultado da busca):
 * duas maneiras de fazer a mesma formatacao divergem na primeira mexida e ninguem
 * delata, que e' a licao da [DA-591].
 */
const CAIXA_ALTA = { textTransform: 'uppercase' } as const;
/** O destaque da casa. Verde e vermelho nao servem de sinal aqui (daltonismo). */
const DESTAQUE = '#fde68a';

// pt-BR: ele digita "149,99". `input type=number` esta proibido pra reais.
function parseMoney(val: string | null | undefined): number {
  if (!val) return 0;
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
}
function fmtMoney(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** Quantidade aceita fracao (3 metros e meio de cabo), mas nao vira zero calado. */
function parseQtd(val: string): number {
  const n = parseFloat(String(val).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function fmtQtd(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}
function fmtData(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Uma linha do documento como a TELA a segura.
 *
 * ⚠️ `key` e' de tela, nunca o id do banco: linha recem-criada ainda nao tem id, e
 * usar o indice do array faria o React embaralhar campos ao apagar uma do meio.
 *
 * ⚠️ `valorTexto` VAZIO nao e' zero — significa "usa o preco do catalogo". E' o
 * mesmo `null` da coluna, e e' o que sustenta a regra do modelo.
 */
interface LinhaLocal {
  key: string;
  tipo: 'material' | 'servico';
  produto_id: number | null;
  descricao: string;
  quantidadeTexto: string;
  valorTexto: string;
  preco_ref: number | null;
  foto_url: string | null;
}

let semente = 0;
function novaChave(): string {
  semente += 1;
  return `l${Date.now()}-${semente}`;
}

function unitario(l: LinhaLocal): number {
  return l.valorTexto.trim() ? parseMoney(l.valorTexto) : (l.preco_ref ?? 0);
}
/**
 * A linha entra no documento?
 *
 * ⚠️ **Mao de obra vale so com o VALOR.** Material sem descricao nao existe, mas
 * quem digita R$ 300 na mao de obra e nao escreve nada esta dizendo "mao de obra:
 * 300" — e o filtro antigo (so `descricao`) jogaria isso fora **em silencio**, com o
 * total na tela contando o dinheiro que o papel nao teria.
 */
function linhaValeNoPapel(l: LinhaLocal): boolean {
  if (l.descricao.trim()) return true;
  return l.tipo === 'servico' && parseMoney(l.valorTexto) > 0;
}

/** O rotulo que vai impresso quando ele nao escreveu nada. */
function descricaoDaLinha(l: LinhaLocal): string {
  return l.descricao.trim() || 'Mão de obra';
}

function totalLinha(l: LinhaLocal): number {
  return unitario(l) * parseQtd(l.quantidadeTexto);
}

interface Contexto {
  titulo: string;
  /** O que a seta do cabecalho faz. `null` = volta pro menu. */
  voltar: (() => void) | null;
}

export function OrcamentosApp({ aoContexto }: { aoContexto: (c: Contexto) => void }): React.ReactElement {
  const [abertoId, setAbertoId] = useState<number | null>(null);

  const voltarPraLista = useCallback(() => setAbertoId(null), []);

  useEffect(() => {
    aoContexto(abertoId === null
      ? { titulo: 'Orçamentos', voltar: null }
      : { titulo: 'Orçamento', voltar: voltarPraLista });
  }, [abertoId, aoContexto, voltarPraLista]);

  if (abertoId === null) return <Lista aoAbrir={setAbertoId} />;
  // ⚠️ `key={abertoId}` NAO e' enfeite: sem ele, trocar de documento (o "Salvar
  // como" abre a copia) reaproveitaria a instancia e o editor ficaria com os campos
  // do anterior — e o salvamento automatico gravaria o conteudo velho no id novo.
  return <Editor key={abertoId} id={abertoId} aoVoltar={voltarPraLista} aoAbrir={setAbertoId} />;
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function Lista({ aoAbrir }: { aoAbrir: (id: number) => void }): React.ReactElement {
  const [aba, setAba] = useState<'orcamentos' | 'modelos'>('orcamentos');
  const [linhas, setLinhas] = useState<ResumoOrcamentoApp[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  /** Modelos oferecidos no toque de "Novo orçamento" (`null` = folha fechada). */
  const [escolhendo, setEscolhendo] = useState<ResumoOrcamentoApp[] | null>(null);
  /** Qual linha esta perguntando "excluir?" — a confirmacao mora NA linha, sem modal. */
  const [confirmando, setConfirmando] = useState<number | null>(null);
  const [excluindo, setExcluindo] = useState<number | null>(null);

  const carregar = useCallback((qualAba: 'orcamentos' | 'modelos') => {
    setLinhas(null);
    setErro(null);
    appApi.orcamentos(qualAba === 'modelos')
      .then(setLinhas)
      .catch((e: ErroApp) => setErro(e.message));
  }, []);

  useEffect(() => { carregar(aba); }, [aba, carregar]);

  /**
   * "Novo orcamento" — **pergunta pelos modelos ANTES de abrir a folha em branco**
   * (pedido dele, 18/08/2026: *"quando clico em modelo reutilizavel, gostaria que o
   * orcamento fosse exibido na hora de criar um novo"*).
   *
   * ⚠️ **So pergunta quem TEM modelo.** Sem nenhum guardado a folha em branco abre
   * direto, como sempre: um passo a mais entre a vontade e o documento e' exatamente
   * o que esta tela nao pode ter. E' o mesmo criterio do [DA-470] com o sinal
   * trocado — a redundancia se paga quando ha o que escolher.
   */
  async function novo(): Promise<void> {
    if (criando) return;
    setCriando(true);
    setErro(null);
    try {
      // Buscado na hora: modelo criado nesta sessao precisa aparecer aqui.
      const modelos = await appApi.orcamentos(true);
      if (modelos.length) { setEscolhendo(modelos); return; }
      const doc = await appApi.criarOrcamento({ cliente_final: null });
      aoAbrir(doc.id);
    } catch (e) {
      setErro((e as ErroApp).message);
    } finally {
      setCriando(false);
    }
  }

  /** Folha em branco, ja com a escolha feita. */
  async function novoEmBranco(): Promise<void> {
    setEscolhendo(null);
    try {
      const doc = await appApi.criarOrcamento({ cliente_final: null });
      aoAbrir(doc.id);
    } catch (e) {
      setErro((e as ErroApp).message);
    }
  }

  /**
   * Excluir da propria lista.
   *
   * ⚠️ **A porta ja existia, mas so DENTRO do documento aberto** — quem quisesse
   * apagar tinha que abrir, rolar ate o pe e voltar. Pedido dele em 18/08.
   *
   * A linha some da tela sem recarregar a lista: recarregar apagaria a aba inteira
   * por um instante pra confirmar o que o servidor acabou de responder.
   */
  async function excluir(id: number): Promise<void> {
    if (excluindo != null) return;
    setExcluindo(id);
    try {
      await appApi.excluirOrcamento(id);
      setLinhas((ls) => (ls ?? []).filter((l) => l.id !== id));
      setConfirmando(null);
    } catch (e) {
      setErro((e as ErroApp).message);
      setConfirmando(null);
    } finally {
      setExcluindo(null);
    }
  }

  /** Usar um modelo = duplicar. O backend repuxa o preco do material do catalogo. */
  async function usarModelo(id: number): Promise<void> {
    try {
      const novoDoc = await appApi.duplicarOrcamento(id, {});
      setEscolhendo(null);
      aoAbrir(novoDoc.id);
    } catch (e) {
      setErro((e as ErroApp).message);
    }
  }

  return (
    <div style={{ paddingBottom: 10 }}>
      <div style={{ padding: 14, backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
        <button
          type="button"
          onClick={() => void novo()}
          disabled={criando}
          style={{
            width: '100%', height: 50, borderRadius: 10, backgroundColor: COBRE, color: '#fff',
            fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, opacity: criando ? 0.6 : 1,
          }}
        >
          <Plus size={20} /> Novo orçamento
        </button>
      </div>

      {/* Duas abas e nao dois itens de menu: modelo e' o mesmo documento com outro
          proposito, e separa-los em telas distantes esconderia justamente o atalho
          que faz a ferramenta valer a pena. */}
      <div style={{ display: 'flex', backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
        {(['orcamentos', 'modelos'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            style={{
              flex: 1, height: 46, fontSize: 14.5, fontWeight: aba === a ? 800 : 600,
              color: aba === a ? COBRE : CINZA,
              borderBottom: aba === a ? `2px solid ${COBRE}` : '2px solid transparent',
              backgroundColor: 'transparent',
            }}
          >
            {a === 'orcamentos' ? 'Meus orçamentos' : 'Modelos'}
          </button>
        ))}
      </div>

      {/* ⚠️ A aba DIZ o que cada toque faz. Sem isto, "Usar" e o toque na linha
          parecem a mesma coisa — e a duvida que isso cria e' justamente se usar um
          modelo gasta o modelo. */}
      {aba === 'modelos' && !!linhas?.length && (
        <div style={{ padding: '10px 16px', fontSize: 12.5, color: CINZA, lineHeight: 1.5, backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
          Toque no modelo para <b>editá-lo</b>. O botão <b>Usar</b> cria uma cópia
          para um cliente — o modelo continua guardado, intacto.
        </div>
      )}

      {erro && (
        <div style={{ margin: 14, padding: 12, borderRadius: 10, backgroundColor: '#fff8ef', border: '1px solid #f0d5b0', fontSize: 13.5, color: '#5b4636' }}>
          {erro}
        </div>
      )}

      {linhas === null ? (
        <div style={{ padding: 20, fontSize: 14, color: CINZA }}>Carregando...</div>
      ) : !linhas.length ? (
        <Vazio aba={aba} />
      ) : (
        <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
          {linhas.map((l) => (confirmando === l.id ? (
            /* A confirmacao substitui a propria linha: o papel que vai sumir e' o que
               fica na tela ate a resposta, e nao um modal por cima de uma lista. */
            <div key={l.id} style={{ borderTop: '1px solid #f0efee', padding: '12px 16px', backgroundColor: '#fff8ef' }}>
              <div style={{ fontSize: 14.5, color: '#111' }}>
                Excluir {aba === 'modelos' ? 'o modelo' : 'o orçamento'}{' '}
                <b>{aba === 'modelos' ? (l.nome_modelo || 'sem nome') : (l.cliente_final || 'sem nome')}</b>?
              </div>
              <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>Não dá pra desfazer.</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
                <button
                  type="button"
                  onClick={() => setConfirmando(null)}
                  style={{ flex: 1, height: 44, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14.5, fontWeight: 700 }}
                >
                  Não
                </button>
                <button
                  type="button"
                  onClick={() => void excluir(l.id)}
                  disabled={excluindo === l.id}
                  style={{ flex: 1, height: 44, borderRadius: 9, border: 'none', backgroundColor: '#b91c1c', color: '#fff', fontSize: 14.5, fontWeight: 700, opacity: excluindo === l.id ? 0.6 : 1 }}
                >
                  {excluindo === l.id ? 'Excluindo...' : 'Sim, excluir'}
                </button>
              </div>
            </div>
          ) : (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #f0efee' }}>
              <button
                type="button"
                onClick={() => aoAbrir(l.id)}
                style={{ flex: 1, minWidth: 0, textAlign: 'left', padding: '13px 6px 13px 16px', color: '#111', backgroundColor: 'transparent' }}
              >
                <div style={{ fontSize: 15.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {aba === 'modelos'
                    ? (l.nome_modelo || 'Modelo sem nome')
                    : (l.cliente_final || 'Sem nome')}
                </div>
                <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>
                  R$ {fmtMoney(l.total)} · {contar(l.itens, 'item', 'itens')} · {fmtData(l.atualizado_em)}
                </div>
              </button>
              {aba === 'modelos' && (
                <button
                  type="button"
                  onClick={() => void usarModelo(l.id)}
                  style={{
                    marginRight: 12, height: 36, padding: '0 14px', borderRadius: 8,
                    border: `1px solid ${COBRE}`, color: COBRE, backgroundColor: '#fff',
                    fontSize: 13.5, fontWeight: 700, flexShrink: 0,
                  }}
                >
                  Usar
                </button>
              )}
              {/* Alvo de 44px: dedo em celular. Cinza e nao vermelho — o vermelho da
                  casa e' de erro, e aqui nada deu errado ainda (o passo perigoso e' o
                  "Sim, excluir", que ai sim vem vermelho). */}
              <button
                type="button"
                onClick={() => setConfirmando(l.id)}
                aria-label={aba === 'modelos' ? 'Excluir modelo' : 'Excluir orçamento'}
                style={{
                  width: 44, height: 44, marginRight: 6, flexShrink: 0, borderRadius: 8,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#a8a29e', backgroundColor: 'transparent',
                }}
              >
                <Trash2 size={18} />
              </button>
            </div>
          )))}
        </div>
      )}

      {escolhendo && (
        <FolhaNovoOrcamento
          modelos={escolhendo}
          aoEmBranco={() => void novoEmBranco()}
          aoUsar={(id) => void usarModelo(id)}
          aoFechar={() => setEscolhendo(null)}
        />
      )}
    </div>
  );
}

/**
 * A escolha na hora de criar: **em branco ou a partir de um modelo**.
 *
 * ⚠️ **O modelo nao se gasta, e a folha DIZ isso** — foi a duvida dele em 18/08
 * (*"ao selecionar o modelo, ele nao e' utilizado, apenas uma copia e'"*). O
 * comportamento ja era esse desde o primeiro dia; o que faltava era a tela contar.
 * Regra do [DA-470] na veia: recurso que existe e nao aparece nao existe.
 */
function FolhaNovoOrcamento({ modelos, aoEmBranco, aoUsar, aoFechar }: {
  modelos: ResumoOrcamentoApp[];
  aoEmBranco: () => void;
  aoUsar: (id: number) => void;
  aoFechar: () => void;
}): React.ReactElement {
  return (
    <Folha titulo="Novo orçamento" aoFechar={aoFechar} rotuloFechar="Cancelar">
      <div style={{ padding: 14, backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
        <button
          type="button"
          onClick={aoEmBranco}
          style={{
            width: '100%', height: 50, borderRadius: 10, backgroundColor: COBRE, color: '#fff',
            fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8,
          }}
        >
          <Plus size={20} /> Começar em branco
        </button>
      </div>

      <div style={{ padding: '14px 16px 8px', fontSize: 12.5, fontWeight: 800, letterSpacing: 0.6, color: '#78716c' }}>
        OU COMECE DE UM MODELO
      </div>

      <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}` }}>
        {modelos.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => aoUsar(m.id)}
            style={{
              width: '100%', textAlign: 'left', padding: '13px 16px', borderTop: '1px solid #f0efee',
              display: 'flex', alignItems: 'center', gap: 10, backgroundColor: '#fff', color: '#111',
            }}
          >
            <Copy size={18} style={{ color: COBRE, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.nome_modelo || 'Modelo sem nome'}
              </div>
              <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>
                {contar(m.itens, 'item', 'itens')}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div style={{ padding: '10px 16px', fontSize: 12.5, color: CINZA, lineHeight: 1.5 }}>
        O modelo continua guardado — você trabalha numa cópia dele. O material entra
        com o preço da loja de hoje.
      </div>
    </Folha>
  );
}

function Vazio({ aba }: { aba: 'orcamentos' | 'modelos' }): React.ReactElement {
  return (
    <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}`, padding: '22px 16px', fontSize: 14.5, color: '#44403c', lineHeight: 1.6 }}>
      {aba === 'orcamentos' ? (
        <>
          Monte aqui o orçamento que <b>você</b> entrega ao seu cliente: o material da
          nossa loja mais a sua mão de obra. Sai em PDF com o <b>seu nome</b> no topo,
          pronto pra mandar por WhatsApp.
        </>
      ) : (
        <>
          Ainda não há modelos. Abra um orçamento pronto e use <b>“Salvar como”</b> pra
          guardá-lo como modelo — na próxima vez ele volta montado, com o preço do
          material atualizado.
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

type EstadoSalvamento = 'limpo' | 'salvando' | 'salvo' | 'erro';

function Editor({ id, aoVoltar, aoAbrir }: {
  id: number;
  aoVoltar: () => void;
  aoAbrir: (id: number) => void;
}): React.ReactElement {
  const [doc, setDoc] = useState<OrcamentoApp | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [cliente, setCliente] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [nomeModelo, setNomeModelo] = useState('');
  const [linhas, setLinhas] = useState<LinhaLocal[]>([]);

  const [estado, setEstado] = useState<EstadoSalvamento>('limpo');
  const [salvarComo, setSalvarComo] = useState(false);
  const [confirmarExcluir, setConfirmarExcluir] = useState(false);

  useEffect(() => {
    let vivo = true;
    appApi.orcamento(id)
      .then((d) => {
        if (!vivo) return;
        setDoc(d);
        setCliente(d.cliente_final ?? '');
        setObservacoes(d.observacoes ?? '');
        setNomeModelo(d.nome_modelo ?? '');
        setLinhas(d.itens.map((i) => ({
          key: novaChave(),
          tipo: i.tipo,
          produto_id: i.produto_id,
          descricao: i.descricao,
          quantidadeTexto: fmtQtd(i.quantidade),
          valorTexto: i.valor_unitario == null ? '' : fmtMoney(i.valor_unitario),
          preco_ref: i.preco_ref,
          foto_url: i.foto_url,
        })));
      })
      .catch((e: ErroApp) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [id]);

  const ehModelo = doc?.modelo === true;
  const materiais = useMemo(() => linhas.filter((l) => l.tipo === 'material'), [linhas]);
  /** A mao de obra e' UMA linha so — texto livre + valor. Ver `mudarMaoDeObra`. */
  const maoDeObra = useMemo(() => linhas.find((l) => l.tipo === 'servico') ?? null, [linhas]);
  const servicos = useMemo(() => linhas.filter((l) => l.tipo === 'servico'), [linhas]);

  /** Quanto de cada produto o papel ja tem. ⚠️ Sai das LINHAS, nao de um contador de
   *  toques: o que a busca precisa dizer e' o que esta no documento — inclusive o que
   *  veio do servidor ao abrir, e o que ele digitou na mao depois. */
  const noOrcamento = useMemo(() => {
    const m: Record<number, number> = {};
    for (const l of materiais) {
      if (l.produto_id != null) m[l.produto_id] = (m[l.produto_id] ?? 0) + parseQtd(l.quantidadeTexto);
    }
    return m;
  }, [materiais]);

  const totais = useMemo(() => {
    const m = Math.round(materiais.reduce((s, l) => s + totalLinha(l), 0) * 100) / 100;
    const s = Math.round(servicos.reduce((a, l) => a + totalLinha(l), 0) * 100) / 100;
    return { material: m, servico: s, total: Math.round((m + s) * 100) / 100 };
  }, [materiais, servicos]);

  // ---- Salvar sozinho ----------------------------------------------------
  //
  // ⚠️ **Nao ha botao "Salvar", e isso e' decisao.** No celular, um botao a mais
  // entre montar e mandar e' um passo a mais em cada orcamento; e um documento que
  // se perde por ele ter fechado o app custa a confianca na ferramenta inteira. O
  // preco e' o estado visivel ao lado do total: "Salvando..." / "Salvo".
  const corpoAtual = useCallback((): CorpoSalvarOrcamento => ({
    cliente_final: cliente.trim() || null,
    observacoes: observacoes.trim() || null,
    nome_modelo: nomeModelo.trim() || null,
    itens: linhas
      .filter(linhaValeNoPapel)
      .map((l) => ({
        tipo: l.tipo,
        produto_id: l.tipo === 'material' ? l.produto_id : null,
        descricao: descricaoDaLinha(l).slice(0, 200),
        // Linha com quantidade apagada nao pode virar 0 no banco (o CHECK recusa) —
        // vale 1 ate ele digitar. A tela ja mostra o campo vazio pra ele preencher.
        quantidade: parseQtd(l.quantidadeTexto) || 1,
        // ⚠️ **Num MODELO, material grava `null` sempre** — nem por acidente ele
        // guarda preco. E' a mesma regra do backend ao duplicar, aplicada tambem na
        // edicao: se um preco entrasse aqui, o modelo voltaria a envelhecer, que e'
        // exatamente o defeito que a regra existe pra impedir.
        valor_unitario: l.tipo === 'servico'
          ? parseMoney(l.valorTexto)
          : ehModelo
            ? null
            : (l.valorTexto.trim() ? parseMoney(l.valorTexto) : null),
      })),
  }), [cliente, observacoes, nomeModelo, linhas, ehModelo]);

  // ⚠️ **Guarda de corrida: só a ULTIMA gravação disparada pode mandar na tela.**
  // Dois PUTs em voo (uma pausa longa, a rede lenta, outra pausa) podem voltar fora
  // de ordem — e o que chega por ultimo escreve o estado. Sem isto, uma resposta
  // velha diria "Salvo" depois de uma falha nova, ou o contrario: exatamente a perda
  // silenciosa que uma tela SEM botao de salvar nao pode ter.
  const gravacao = useRef(0);
  const salvarAgora = useCallback(async (): Promise<void> => {
    const minha = ++gravacao.current;
    setEstado('salvando');
    try {
      await appApi.salvarOrcamento(id, corpoAtual());
      if (minha === gravacao.current) setEstado('salvo');
    } catch (e) {
      if (minha !== gravacao.current) return;
      setEstado('erro');
      setErro((e as ErroApp).message);
    }
  }, [id, corpoAtual]);

  // O relogio so comeca depois que o documento chegou — senao o primeiro `useEffect`
  // gravaria o estado vazio por cima do que acabou de ser lido.
  const carregado = doc !== null;
  const primeiro = useRef(true);
  useEffect(() => {
    if (!carregado) return;
    if (primeiro.current) { primeiro.current = false; return; }
    setEstado('salvando');
    const t = setTimeout(() => { void salvarAgora(); }, 900);
    return () => clearTimeout(t);
  }, [carregado, salvarAgora]);

  function mudar(key: string, campo: keyof LinhaLocal, valor: string): void {
    setLinhas((ls) => ls.map((l) => (l.key === key ? { ...l, [campo]: valor } : l)));
  }
  function remover(key: string): void {
    setLinhas((ls) => ls.filter((l) => l.key !== key));
  }

  /**
   * Acrescentar material.
   *
   * ⚠️ **Produto que JA esta no papel soma na quantidade — nao vira linha repetida**
   * (18/08/2026). Ele tocou uma vez num cabo e o orcamento saiu com **tres linhas
   * iguais de 1 metro**; a versao anterior tratava cada toque como um item novo, e
   * um toque a mais (ou um encostar de dedo rolando a lista) virava sujeira num
   * documento que o cliente DELE le. Somar na quantidade torna o toque repetido
   * inofensivo, e "3 metros" e' o que ele escreveria a mao de qualquer jeito.
   */
  function adicionarMaterial(p: ProdutoBusca): void {
    setLinhas((ls) => {
      const i = ls.findIndex((l) => l.tipo === 'material' && l.produto_id === p.id);
      const atual = i >= 0 ? ls[i] : undefined;
      if (atual) {
        const copia = [...ls];
        copia[i] = { ...atual, quantidadeTexto: fmtQtd(parseQtd(atual.quantidadeTexto) + 1) };
        return copia;
      }
      return [...ls, {
        key: novaChave(),
        tipo: 'material',
        produto_id: p.id,
        descricao: p.descricao,
        quantidadeTexto: '1',
        valorTexto: fmtMoney(p.preco),
        preco_ref: p.preco,
        foto_url: p.foto_url,
      }];
    });
  }

  /**
   * A mao de obra e' **UM texto e UM valor**, nao uma lista de itens (18/08/2026).
   *
   * ⚠️ A versao anterior pedia "+ Adicionar" pra so entao abrir uma linha com
   * descricao, quantidade e unitario — a mesma forma do material. Ele leu a tela e
   * nao entendeu: *"aqui pra mim ficou confuso"*, e chutou que o botao servia pra
   * editar o subtotal. Chute de usuario sobre o que um botao faz e' medida de
   * clareza, nao engano dele. E a forma que ele descreveu e' a que a decisao
   * original ja pedia — *mao de obra LIVRE, sem forma fixa* ([DA-565]).
   *
   * ⛔ Nao havia o que migrar: **zero linhas de servico em toda a base** quando isto
   * foi trocado (conferido no banco, nao presumido).
   */
  function mudarMaoDeObra(campo: 'descricao' | 'valorTexto', valor: string): void {
    setLinhas((ls) => {
      const i = ls.findIndex((l) => l.tipo === 'servico');
      const atual = i >= 0 ? ls[i] : undefined;
      if (atual) {
        const copia = [...ls];
        copia[i] = { ...atual, [campo]: valor };
        return copia;
      }
      return [...ls, {
        key: novaChave(),
        tipo: 'servico',
        produto_id: null,
        descricao: campo === 'descricao' ? valor : '',
        quantidadeTexto: '1',
        valorTexto: campo === 'valorTexto' ? valor : '',
        preco_ref: null,
        foto_url: null,
      }];
    });
  }

  async function excluir(): Promise<void> {
    try {
      await appApi.excluirOrcamento(id);
      aoVoltar();
    } catch (e) {
      setErro((e as ErroApp).message);
      setConfirmarExcluir(false);
    }
  }

  if (erro && !doc) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ padding: 14, borderRadius: 10, backgroundColor: '#fff8ef', border: '1px solid #f0d5b0', fontSize: 14, color: '#5b4636' }}>
          {erro}
        </div>
      </div>
    );
  }
  if (!doc) return <div style={{ padding: 20, fontSize: 14, color: CINZA }}>Carregando...</div>;

  const semItens = linhas.filter(linhaValeNoPapel).length === 0;

  return (
    <div style={{ paddingBottom: 16 }}>
      {/* ---- Cabecalho do documento ---- */}
      <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}`, padding: 14 }}>
        <label style={{ display: 'block' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>
            {doc.modelo ? 'Nome do modelo' : 'Cliente'}
          </span>
          <input
            className="no-upper"
            style={{ ...estiloCampo, marginTop: 6 }}
            value={doc.modelo ? nomeModelo : cliente}
            onChange={(e) => (doc.modelo ? setNomeModelo(e.target.value) : setCliente(e.target.value))}
            placeholder={doc.modelo ? 'Ex.: Instalação de chuveiro' : 'Para quem é este orçamento'}
          />
        </label>
        {doc.modelo && (
          // ⚠️ O modelo DIZ que nao guarda preco de material. Sem esta linha ele
          // guardaria a impressao contraria ao ver os valores na tela — que sao os
          // do catalogo de hoje, nao os de quando o modelo nasceu.
          <div style={{ marginTop: 9, fontSize: 12.5, color: '#5b4636', backgroundColor: '#fff8ef', border: '1px solid #f0d5b0', borderRadius: 8, padding: '9px 11px', lineHeight: 1.5 }}>
            Este é um <b>modelo</b>: ele guarda os itens, não os preços. O material é
            mostrado sempre pelo preço de hoje na loja.
          </div>
        )}
      </div>

      {/* ---- Material ----
          ⚠️ **Nao ha cabecalho "MATERIAL" com botao: o campo de busca E' o cabecalho**
          (pedido dele em 18/08, com o app na mao). O botao abria uma folha por cima
          da tela — um toque a mais pra comecar, outro pra sair, e a lista do
          orcamento sumindo de vista enquanto ele escolhia. Aqui ele digita direto.
          A separacao das duas secoes continua existindo pelo "Subtotal · Material" e
          pelo cabecalho de Mao de obra logo abaixo. */}
      <BuscaMaterialInline aoAdicionar={adicionarMaterial} noOrcamento={noOrcamento} />
      {materiais.length === 0 ? (
        /* ⚠️ Este texto MANDAVA tocar num botao que a busca inline aposentou — a
           frase sobreviveu a peca que ela descrevia. Agora aponta pro campo acima. */
        <SemLinhas texto="Nenhum material ainda. Busque no campo acima." />
      ) : (
        <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
          {materiais.map((l) => (
            <LinhaMaterial key={l.key} linha={l} modelo={ehModelo} aoMudar={mudar} aoRemover={remover} />
          ))}
        </div>
      )}
      <Subtotal rotulo="Material" valor={totais.material} />

      {/* ---- Mao de obra ----
          ⛔ Sem catalogo, sem categoria e sem forma fixa — decisao do Ruan:
          *"flexivel, para que ele possa ajustar da forma que julgar necessario"*.
          O campo ja NASCE aberto: nao ha o que "adicionar" antes de escrever. */}
      <Secao icone={<Wrench size={16} />} titulo="Mão de obra" />
      <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: 14 }}>
        <textarea
          className="no-upper"
          value={maoDeObra?.descricao ?? ''}
          onChange={(e) => mudarMaoDeObra('descricao', e.target.value)}
          rows={3}
          aria-label="Descrição da mão de obra"
          placeholder="Descreva o serviço: o que será feito, o que está incluso, prazo."
          /* Corretor ligado — mesma regra (e o mesmo porque) das Observacoes. */
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
          style={{
            width: '100%', padding: 12, fontSize: 16, borderRadius: 10,
            border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#111', resize: 'vertical',
          }}
        />
      </div>
      <SubtotalMaoDeObra
        valorTexto={maoDeObra?.valorTexto ?? ''}
        aoMudar={(v) => mudarMaoDeObra('valorTexto', v)}
      />

      {/* ---- Total ---- */}
      <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: '14px 16px', marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.4, color: '#44403c' }}>TOTAL</span>
          <span style={{ fontSize: 26, fontWeight: 800, color: '#111' }}>R$ {fmtMoney(totais.total)}</span>
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: CINZA }}>
          {estado === 'salvando' ? 'Salvando...'
            : estado === 'erro' ? 'Não conseguimos salvar agora — a última alteração pode se perder.'
            : estado === 'salvo' ? 'Salvo'
            : 'Salvo'}
        </div>
      </div>

      {/* ---- Observacoes ---- */}
      <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}`, padding: 14, marginTop: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>Observações</span>
        <textarea
          className="no-upper"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          rows={3}
          /* ⚠️ **No iPhone, SUGERIR e TROCAR sao o MESMO interruptor** — apurado na
             doc da Apple em 18/08/2026, depois de eu ter escrito aqui que eram dois:
             *"autocorrect: If off, deactivates automatic correction for a form or
             input element; on, activates"*. Nao ha atributo que peca "sugira mas nao
             troque". Ele pediu a troca desligada, viu a sugestao sumir junto
             (*"digitei instalacao e o app nao mostrou a sugestao"*) e, posto o custo,
             escolheu ligar de volta. ⛔ Nao desligar de novo por zelo: a saida
             quando o aparelho quer trocar e' **por palavra**, tocando na opcao entre
             aspas da barra do teclado, que e' a original. */
          autoCapitalize="sentences"
          autoCorrect="on"
          spellCheck
          placeholder="Prazo, condições, garantia..."
          style={{
            width: '100%', marginTop: 6, padding: 12, fontSize: 16, borderRadius: 10,
            border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#111', resize: 'vertical',
          }}
        />
      </div>

      {/* ---- Acoes ---- */}
      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <BotaoCompartilharOrcamento
          montarDoc={() => ({
            ...doc,
            cliente_final: cliente.trim() || null,
            observacoes: observacoes.trim() || null,
            nome_modelo: nomeModelo.trim() || null,
            itens: linhas
              .filter(linhaValeNoPapel)
              .map((l, idx) => ({
                id: idx,
                tipo: l.tipo,
                produto_id: l.produto_id,
                descricao: descricaoDaLinha(l),
                quantidade: parseQtd(l.quantidadeTexto) || 1,
                valor_unitario: l.valorTexto.trim() ? parseMoney(l.valorTexto) : null,
                preco_ref: l.preco_ref,
                foto_url: l.foto_url,
                ordem: idx,
              })),
            totais,
          })}
          bloqueio={semItens ? 'Adicione ao menos um item para poder compartilhar.' : null}
          aoAntesDeGerar={() => { void salvarAgora(); }}
        />

        <button
          type="button"
          onClick={() => setSalvarComo(true)}
          style={{
            height: 48, borderRadius: 10, border: `1px solid ${COBRE}`, backgroundColor: '#fff',
            color: COBRE, fontSize: 15.5, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <Copy size={18} /> Salvar como
        </button>

        {!confirmarExcluir ? (
          <button
            type="button"
            onClick={() => setConfirmarExcluir(true)}
            style={{
              height: 44, borderRadius: 10, border: '1px solid #d6d3d1', backgroundColor: '#fff',
              color: '#78716c', fontSize: 14.5, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            }}
          >
            <Trash2 size={16} /> Excluir
          </button>
        ) : (
          <div style={{ border: '1px solid #d6d3d1', borderRadius: 10, padding: 12, backgroundColor: '#fff' }}>
            <div style={{ fontSize: 14.5, color: '#111' }}>Excluir este orçamento?</div>
            <div style={{ fontSize: 12.5, color: CINZA, marginTop: 3 }}>Não dá pra desfazer.</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 11 }}>
              <button
                type="button"
                onClick={() => setConfirmarExcluir(false)}
                style={{ flex: 1, height: 44, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 14.5, fontWeight: 700 }}
              >
                Não
              </button>
              <button
                type="button"
                onClick={() => void excluir()}
                style={{ flex: 1, height: 44, borderRadius: 9, backgroundColor: '#b91c1c', color: '#fff', fontSize: 14.5, fontWeight: 700 }}
              >
                Excluir
              </button>
            </div>
          </div>
        )}

        {erro && doc && (
          <div style={{ padding: 11, borderRadius: 9, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
            {erro}
          </div>
        )}
      </div>

      {salvarComo && (
        <FolhaSalvarComo
          id={id}
          clienteAtual={cliente}
          aoAntes={salvarAgora}
          aoFechar={() => setSalvarComo(false)}
          aoCriado={(novoId) => { setSalvarComo(false); aoAbrir(novoId); }}
        />
      )}
    </div>
  );
}

function Secao({ icone, titulo, acao, aoAgir }: {
  icone: React.ReactNode; titulo: string;
  /** Opcional: hoje so o cabecalho de Mao de obra usa a versao sem botao. */
  acao?: string; aoAgir?: () => void;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 16px 8px' }}>
      <span style={{ color: COBRE, display: 'flex' }}>{icone}</span>
      <span style={{ flex: 1, fontSize: 12.5, fontWeight: 800, letterSpacing: 0.6, color: '#78716c', textTransform: 'uppercase' }}>
        {titulo}
      </span>
      {acao && aoAgir && (
        <button
          type="button"
          onClick={aoAgir}
          style={{
            height: 34, padding: '0 12px', borderRadius: 8, border: `1px solid ${COBRE}`,
            backgroundColor: '#fff', color: COBRE, fontSize: 13, fontWeight: 700,
            display: 'flex', alignItems: 'center', gap: 5,
          }}
        >
          <Plus size={15} /> {acao}
        </button>
      )}
    </div>
  );
}

function SemLinhas({ texto }: { texto: string }): React.ReactElement {
  return (
    <div style={{ backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`, borderBottom: `1px solid ${BORDA}`, padding: '15px 16px', fontSize: 14, color: CINZA }}>
      {texto}
    </div>
  );
}

function Subtotal({ rotulo, valor }: { rotulo: string; valor: number }): React.ReactElement {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 16px 0', fontSize: 13.5, color: '#44403c' }}>
      <span>Subtotal · {rotulo}</span>
      <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>R$ {fmtMoney(valor)}</span>
    </div>
  );
}

/**
 * O subtotal da mao de obra — **editavel pelo lapis** (pedido dele, 18/08/2026:
 * *"troque o botao pelo icone de um lapis, ao lado do valor"*).
 *
 * ⚠️ Aqui o subtotal NAO e' uma soma: e' o valor que ele cobra, digitado. Por isso
 * ele se edita, enquanto o do material — que soma linhas — continua so-leitura. O
 * lapis fica sempre visivel: campo que so se revela ao tocar no lugar certo e'
 * recurso que nao existe pra quem nao adivinhou.
 */
function SubtotalMaoDeObra({ valorTexto, aoMudar }: {
  valorTexto: string; aoMudar: (v: string) => void;
}): React.ReactElement {
  const [editando, setEditando] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editando) campo.current?.focus(); }, [editando]);

  return (
    /* ⚠️ O respiro de cima encolheu (9 -> 3px) porque o BOTAO cresceu: medido na
       tela publicada, o alvo do lapis dava 28px de altura — abaixo do dedo. Os
       10px verticais dele levam o toque a 40px sem inchar a faixa. */
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '3px 16px 0', fontSize: 13.5, color: '#44403c' }}>
      <span>Subtotal · Mão de obra</span>
      {editando ? (
        <input
          ref={campo}
          className="no-upper"
          inputMode="decimal"
          value={valorTexto}
          onChange={(e) => aoMudar(e.target.value)}
          onBlur={(e) => {
            aoMudar(e.target.value.trim() ? fmtMoney(parseMoney(e.target.value)) : '');
            setEditando(false);
          }}
          aria-label="Valor da mão de obra"
          style={{ ...estiloCampoLinha, width: 130, textAlign: 'right', fontWeight: 700 }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditando(true)}
          aria-label="Editar o valor da mão de obra"
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 2px', backgroundColor: 'transparent', color: '#44403c' }}
        >
          <span style={{ fontSize: 13.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            R$ {fmtMoney(parseMoney(valorTexto))}
          </span>
          <Pencil size={16} style={{ color: COBRE }} />
        </button>
      )}
    </div>
  );
}

const estiloCampoLinha: React.CSSProperties = {
  height: 44, padding: '0 10px', fontSize: 16, border: '1px solid #d1d5db',
  borderRadius: 8, backgroundColor: '#fff', color: '#111', minWidth: 0,
};

/**
 * Uma linha de material.
 *
 * ⚠️ **O preco do catalogo aparece embaixo como REFERENCIA — o que vale no papel e'
 * o que ELE digitou.** Duas coisas dependem disso: ele cobra do cliente dele o preco
 * que quiser (e' o negocio dele), e um orcamento antigo mostra na cara que a loja
 * mudou o preco desde entao, sem ninguem reescrever o numero dele por conta propria.
 *
 * ⚠️ **Dentro de um MODELO o campo de valor nao existe** — la o preco e' sempre o do
 * catalogo de hoje, e um campo editavel convidaria a congelar um numero que o modelo
 * inteiro foi feito pra nao guardar. O ajuste de preco acontece no orcamento que sai
 * do modelo, que e' onde ele vale.
 */
function LinhaMaterial({ linha, modelo, aoMudar, aoRemover }: {
  linha: LinhaLocal;
  modelo: boolean;
  aoMudar: (key: string, campo: keyof LinhaLocal, valor: string) => void;
  aoRemover: (key: string) => void;
}): React.ReactElement {
  const ref = linha.preco_ref;
  const digitado = linha.valorTexto.trim() ? parseMoney(linha.valorTexto) : null;
  const divergente = !modelo && ref != null && digitado != null && Math.abs(digitado - ref) >= 0.01;

  return (
    <div style={{ borderTop: '1px solid #f0efee', padding: '11px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {/* Aqui a foto CONFERE (na busca ela escolhe), entao ela e' menor: o que a
            linha precisa mostrar de verdade sao quantidade e valor, logo abaixo. */}
        {linha.foto_url && (
          <FotoProduto url={linha.foto_url} tam={38} style={{ flexShrink: 0, marginTop: 1 }} />
        )}
        <div style={{ flex: 1, minWidth: 0, fontSize: 15, color: '#111', lineHeight: 1.35, ...CAIXA_ALTA }}>
          {linha.descricao}
        </div>
        <button
          type="button"
          onClick={() => aoRemover(linha.key)}
          aria-label="Remover item"
          /* ⚠️ Lixeira, nao X (pedido dele, 18/08/2026). O X do orcamento ficava a
             um toque do X que FECHA a tela, no mesmo canto direito — dois simbolos
             iguais pra "sair" e "apagar". A lixeira nomeia o que o toque faz. */
          style={{ width: 38, height: 38, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a8a29e' }}
        >
          <Trash2 size={17} />
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
        <input
          className="no-upper"
          inputMode="decimal"
          value={linha.quantidadeTexto}
          onChange={(e) => aoMudar(linha.key, 'quantidadeTexto', e.target.value)}
          aria-label="Quantidade"
          style={{ ...estiloCampoLinha, width: 66, textAlign: 'center' }}
        />
        <span style={{ fontSize: 14, color: CINZA }}>×</span>
        {modelo ? (
          <span style={{ flex: 1, fontSize: 15, color: '#44403c', fontVariantNumeric: 'tabular-nums' }}>
            R$ {fmtMoney(ref ?? 0)}
          </span>
        ) : (
          <input
            className="no-upper"
            inputMode="decimal"
            value={linha.valorTexto}
            onChange={(e) => aoMudar(linha.key, 'valorTexto', e.target.value)}
            onBlur={(e) => aoMudar(linha.key, 'valorTexto', e.target.value.trim() ? fmtMoney(parseMoney(e.target.value)) : '')}
            aria-label="Valor unitário"
            style={{ ...estiloCampoLinha, flex: 1 }}
          />
        )}
        <span style={{ fontSize: 15, fontWeight: 700, color: '#111', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          R$ {fmtMoney(totalLinha(linha))}
        </span>
      </div>

      {ref != null && (
        <div style={{ marginTop: 5, fontSize: 11.5, color: CINZA }}>
          {modelo ? 'Preço da loja hoje' : `Catálogo hoje: R$ ${fmtMoney(ref)}`}
          {divergente && ' · você mudou o valor'}
        </div>
      )}
    </div>
  );
}

// ⛔ Nao ha mais `LinhaServico`: a mao de obra deixou de ser lista de itens e virou
// UM texto + UM valor, editados na propria tela (ver `mudarMaoDeObra` e
// `SubtotalMaoDeObra`). Reintroduzir linhas aqui traz de volta a tela que ele leu e
// nao entendeu.

// ---------------------------------------------------------------------------
// Folhas (painel que cobre a tela)
// ---------------------------------------------------------------------------

/**
 * ⚠️ Convencao da casa: **nao fecha por toque no fundo**, e tem DOIS caminhos de
 * saida visiveis (o X no topo e o botao do rodape) — no celular nao existe Esc, e um
 * caminho unico e' ponto unico de falha ([DA-458]).
 */
function Folha({ titulo, aoFechar, rotuloFechar = 'Fechar', children }: {
  titulo: string; aoFechar: () => void; rotuloFechar?: string; children: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, backgroundColor: '#f7f7f7', color: '#111', display: 'flex', flexDirection: 'column' }}>
      <header style={{ backgroundColor: COBRE, color: '#fff', padding: '12px 8px 12px 16px', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <div style={{ flex: 1, fontSize: 17, fontWeight: 800, letterSpacing: 0.3 }}>{titulo}</div>
        <button type="button" onClick={aoFechar} aria-label="Fechar" style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
          <X size={22} />
        </button>
      </header>
      <div style={{ flex: 1, overflowY: 'auto' }}>{children}</div>
      <div style={{ padding: 14, flexShrink: 0, borderTop: `1px solid ${BORDA}`, backgroundColor: '#fff' }}>
        <button
          type="button"
          onClick={aoFechar}
          style={{ width: '100%', height: 48, borderRadius: 10, border: '1px solid #d6d3d1', backgroundColor: '#fff', color: '#44403c', fontSize: 15.5, fontWeight: 700 }}
        >
          {rotuloFechar}
        </button>
      </div>
    </div>
  );
}

/**
 * A busca de material — **dentro da propria tela**, no lugar onde havia o cabecalho
 * "MATERIAL" com o botao "Buscar no catálogo" (trocado a pedido dele, 18/08/2026).
 *
 * ⚠️ **Ela FECHA ao escolher** (ordem dele, 18/08/2026 — antes ficava aberta pra
 * emendar varios itens). O ganho nao e' so o toque a menos: com a lista fechada, o
 * material que acabou de entrar aparece na hora logo abaixo, e era justamente nao
 * ver o item cair que fazia o mesmo produto ser tocado varias vezes.
 *
 * ⚠️ **Os resultados tem altura maxima e rolam por dentro.** Sem isso, 30 achados
 * empurrariam o orcamento inteiro pra baixo da tela, e ele perderia de vista
 * justamente o que esta montando.
 */
function BuscaMaterialInline({ aoAdicionar, noOrcamento }: {
  aoAdicionar: (p: ProdutoBusca) => void;
  /** Quanto de cada produto ja esta no papel, por `produto_id`. */
  noOrcamento: Record<number, number>;
}): React.ReactElement {
  const [q, setQ] = useState('');
  const [itens, setItens] = useState<ProdutoBusca[] | null>(null);
  const [procurando, setProcurando] = useState(false);
  /** Os achados estao a mostra? Fecha ao escolher — ordem dele, 18/08/2026. */
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    const termo = q.trim();
    if (termo.length < 2) { setItens(null); setProcurando(false); setAberto(false); return; }
    setProcurando(true);
    setAberto(true);
    const t = setTimeout(() => {
      appApi.buscarMaterial(termo)
        .then(setItens)
        .catch(() => setItens([]))
        .finally(() => setProcurando(false));
    }, 320);
    return () => clearTimeout(t);
  }, [q]);

  /**
   * Escolher **fecha** a lista (*"Quando clicar no '+' ele deve selecionar e fechar
   * o combo"*).
   *
   * ⚠️ O termo digitado FICA no campo: fechar nao e' apagar. Tocar no campo de novo
   * reabre os mesmos achados, sem rebuscar — quem procurou "fendido" costuma querer
   * o proximo diametro em seguida, e obriga-lo a redigitar seria trocar um incomodo
   * por outro. O X limpa de vez.
   */
  function escolher(p: ProdutoBusca): void {
    aoAdicionar(p);
    setAberto(false);
  }

  const termo = q.trim();

  return (
    <div>
      <div style={{ padding: '16px 16px 10px' }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: 17, color: COBRE }} />
          <input
            className="no-upper"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            /* Voltar ao campo reabre o que ele ja tinha achado. */
            onFocus={() => { if (itens?.length) setAberto(true); }}
            placeholder="Buscar material no catálogo"
            /* ⛔ Sem `autoFocus`: o campo agora nasce junto com a tela, e abrir o
               teclado sozinho esconderia metade do orcamento a cada visita. */
            style={{ ...estiloCampo, paddingLeft: 38, paddingRight: q ? 40 : undefined }}
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ('')}
              aria-label="Limpar busca"
              style={{
                position: 'absolute', right: 4, top: 6, width: 36, height: 36,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#a8a29e', backgroundColor: 'transparent',
              }}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {termo.length === 0 || !aberto ? null : termo.length < 2 ? (
        <div style={{ padding: '2px 16px 12px', fontSize: 13.5, color: CINZA }}>
          Digite ao menos 2 letras.
        </div>
      ) : procurando && itens === null ? (
        <div style={{ padding: '18px 16px', fontSize: 14, color: CINZA }}>Procurando...</div>
      ) : itens && itens.length === 0 ? (
        <div style={{ padding: '2px 16px 14px', fontSize: 14, color: CINZA }}>
          Nada encontrado com “{termo}”. Tente outro nome ou o código do produto.
        </div>
      ) : (
        <div style={{
          backgroundColor: '#fff', borderTop: `1px solid ${BORDA}`,
          borderBottom: `1px solid ${BORDA}`, maxHeight: '46vh', overflowY: 'auto',
        }}>
          {(itens ?? []).map((p) => {
            const posto = noOrcamento[p.id] ?? 0;
            return (
              /* ⚠️ **A LINHA nao e' mais o botao** (18/08/2026). Ela inteira era um
                 `<button>`, entao qualquer toque nela — inclusive o dedo que so queria
                 rolar a lista — acrescentava material. Quem acrescenta agora e' so o
                 `+` a direita, com alvo de 46px. */
              <div
                key={p.id}
                style={{
                  padding: '11px 10px 11px 16px',
                  borderTop: '1px solid #f0efee', display: 'flex', alignItems: 'center', gap: 10,
                  backgroundColor: posto ? DESTAQUE : '#fff',
                  color: posto ? '#000' : '#111',
                }}
              >
                {p.foto_url ? (
                  <FotoProduto url={p.foto_url} tam={46} style={{ flexShrink: 0 }} />
                ) : (
                  /* Sem foto sao DOIS produtos em 1.503 — mas a moldura vazia fica, senao a
                     linha deles nasce desalinhada de todas as outras. */
                  <div style={{
                    width: 46, height: 46, flexShrink: 0, borderRadius: 4,
                    border: `1px dashed ${BORDA}`, backgroundColor: '#fafaf9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <PackageSearch size={18} style={{ color: '#d6d3d1' }} />
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, lineHeight: 1.35, ...CAIXA_ALTA }}>{p.descricao}</div>
                  <div style={{ fontSize: 12.5, color: posto ? '#4b3f10' : CINZA, marginTop: 3 }}>
                    R$ {fmtMoney(p.preco)}{p.unidade ? ` / ${p.unidade}` : ''}
                    {p.codigo_interno ? ` · ${p.codigo_interno}` : ''}
                  </div>
                  {/* ⚠️ O que ja esta no papel sai da propria LINHA do orcamento, e nao
                      de um contador de toques: se ele mexer na quantidade a mao, este
                      numero acompanha. */}
                  {posto > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: 700, color: '#000', marginTop: 3 }}>
                      <Check size={14} /> {fmtQtd(posto)} no orçamento
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => escolher(p)}
                  aria-label={`Adicionar ${p.descricao}`}
                  style={{
                    width: 46, height: 46, flexShrink: 0, borderRadius: 10,
                    border: `1px solid ${COBRE}`, backgroundColor: '#fff', color: COBRE,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <Plus size={22} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* O preco vem da loja: dito UMA vez, embaixo do que ele acabou de ver, e nao
          como paragrafo permanente ocupando o topo da tela. */}
      {aberto && itens && itens.length > 0 && (
        <div style={{ padding: '8px 16px 2px', fontSize: 12, color: CINZA, lineHeight: 1.45 }}>
          O preço é o da nossa loja hoje — entra como sugestão e você ajusta.
        </div>
      )}
    </div>
  );
}

/**
 * "Salvar como" — a peca CENTRAL da fase 1.
 *
 * ⚠️ **O rotulo e' esse e nao muda** ("Duplicar", "Kit", "Modelo" foram descartados
 * — a palavra e' dele). Duas saidas na mesma folha porque a escolha e' a mesma
 * pergunta: *este papel vira o de outro cliente, ou vira a base dos proximos?*
 */
function FolhaSalvarComo({ id, clienteAtual, aoAntes, aoFechar, aoCriado }: {
  id: number;
  clienteAtual: string;
  aoAntes: () => Promise<void>;
  aoFechar: () => void;
  aoCriado: (novoId: number) => void;
}): React.ReactElement {
  const [modo, setModo] = useState<'copia' | 'modelo'>('copia');
  const [cliente, setCliente] = useState('');
  const [nome, setNome] = useState(clienteAtual.trim());
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const bloqueio = modo === 'modelo' && !nome.trim() ? 'Dê um nome ao modelo para continuar.' : null;

  async function criar(): Promise<void> {
    if (bloqueio || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      // ⚠️ Salva o que esta na tela ANTES de duplicar: a copia sai do BANCO, e sem
      // isto ela nasceria sem a ultima linha que ele acabou de digitar.
      await aoAntes();
      const novo = await appApi.duplicarOrcamento(id, modo === 'modelo'
        ? { modelo: true, nome_modelo: nome.trim() }
        : { cliente_final: cliente.trim() });
      aoCriado(novo.id);
    } catch (e) {
      setErro((e as ErroApp).message);
      setOcupado(false);
    }
  }

  return (
    <Folha titulo="Salvar como" aoFechar={aoFechar} rotuloFechar="Cancelar">
      <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
        <Escolha
          marcado={modo === 'copia'}
          aoMarcar={() => setModo('copia')}
          titulo="Cópia para outro cliente"
          ajuda="Mesmo material e mesma mão de obra, com os valores que você já ajustou."
        />
        <Escolha
          marcado={modo === 'modelo'}
          aoMarcar={() => setModo('modelo')}
          titulo="Modelo reutilizável"
          ajuda="Guarda os itens, não os preços. Ao usar o modelo, o material volta com o preço da loja naquele dia."
        />
      </div>

      <div style={{ padding: 14, backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}`, marginTop: 10 }}>
        {modo === 'copia' ? (
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>Cliente (pode deixar em branco)</span>
            <input
              className="no-upper"
              style={{ ...estiloCampo, marginTop: 6 }}
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Para quem é a cópia"
            />
          </label>
        ) : (
          <label style={{ display: 'block' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: CINZA }}>Nome do modelo</span>
            <input
              className="no-upper"
              autoFocus
              style={{ ...estiloCampo, marginTop: 6 }}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex.: Troca de disjuntor"
            />
          </label>
        )}
      </div>

      <div style={{ padding: 14 }}>
        <button
          type="button"
          onClick={() => void criar()}
          disabled={bloqueio !== null || ocupado}
          style={{
            width: '100%', height: 50, borderRadius: 10, backgroundColor: COBRE, color: '#fff',
            fontSize: 16, fontWeight: 700, opacity: bloqueio || ocupado ? 0.5 : 1,
          }}
        >
          {ocupado ? 'Criando...' : modo === 'modelo' ? 'Guardar modelo' : 'Criar cópia'}
        </button>
        {/* Botao travado DIZ por que — cinza e mudo e' beco sem saida. */}
        {bloqueio && (
          <div style={{ marginTop: 8, fontSize: 13, color: '#b45309' }}>{bloqueio}</div>
        )}
        {erro && (
          <div style={{ marginTop: 8, padding: 11, borderRadius: 9, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13 }}>
            {erro}
          </div>
        )}
      </div>
    </Folha>
  );
}

function Escolha({ marcado, aoMarcar, titulo, ajuda }: {
  marcado: boolean; aoMarcar: () => void; titulo: string; ajuda: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={marcado}
      onClick={aoMarcar}
      style={{
        width: '100%', textAlign: 'left', padding: '14px 16px', borderTop: '1px solid #f0efee',
        display: 'flex', alignItems: 'flex-start', gap: 11,
        backgroundColor: marcado ? DESTAQUE : '#fff', color: marcado ? '#000' : '#111',
      }}
    >
      <span style={{
        width: 20, height: 20, marginTop: 2, borderRadius: 999, flexShrink: 0,
        border: `2px solid ${marcado ? '#000' : '#a8a29e'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {marcado && <span style={{ width: 10, height: 10, borderRadius: 999, backgroundColor: '#000' }} />}
      </span>
      <span style={{ flex: 1 }}>
        <span style={{ display: 'block', fontSize: 15.5, fontWeight: 700 }}>{titulo}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: marcado ? '#4b3f10' : CINZA, marginTop: 3, lineHeight: 1.5 }}>
          {ajuda}
        </span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Compartilhar
// ---------------------------------------------------------------------------

/**
 * O PDF sai pelo COMPARTILHAMENTO NATIVO do aparelho — mesmo mecanismo ja provado no
 * iPhone dele ([DA-524], [DA-558]).
 *
 * ⚠️ **O PDF comeca a ser montado no TOQUE (`onPointerDown`), nao no clique.** O
 * Safari so aceita `navigator.share` enquanto durar o gesto; carregar o `jspdf` e
 * desenhar o documento no meio do caminho gasta essa janela e devolve
 * `NotAllowedError` com cara de defeito aleatorio. ⛔ Nao "simplificar" movendo tudo
 * pro `onClick`.
 *
 * ⚠️ **O papel sai do que esta NA TELA, nao do banco.** Assim ele nunca compartilha
 * uma versao anterior porque o salvamento automatico ainda nao tinha rodado — e o
 * salvamento e' disparado em paralelo, nao no caminho critico.
 */
function BotaoCompartilharOrcamento({ montarDoc, bloqueio, aoAntesDeGerar }: {
  montarDoc: () => OrcamentoApp;
  bloqueio: string | null;
  aoAntesDeGerar: () => void;
}): React.ReactElement {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const arquivoRef = useRef<Promise<File | null> | null>(null);

  function preparar(): Promise<File | null> {
    if (bloqueio) return Promise.resolve(null);
    if (!arquivoRef.current) {
      const doc = montarDoc();
      arquivoRef.current = gerarPdfDoPrestador(doc).catch(() => null);
    }
    return arquivoRef.current;
  }

  function baixar(arquivo: File): void {
    const url = URL.createObjectURL(arquivo);
    const a = document.createElement('a');
    a.href = url;
    a.download = arquivo.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function compartilhar(): Promise<void> {
    if (bloqueio || ocupado) return;
    setOcupado(true);
    setErro(null);
    aoAntesDeGerar();
    try {
      const arquivo = await preparar();
      if (!arquivo) {
        // Falhou montar o PDF: o proximo toque tenta de novo (por isso a promessa
        // guardada e' descartada) e a tela nao fica muda.
        arquivoRef.current = null;
        setErro('Não conseguimos gerar o PDF agora. Tente de novo.');
        return;
      }
      const nav = navigator as Navigator & {
        share?: (d: { files?: File[]; title?: string }) => Promise<void>;
        canShare?: (d: { files?: File[] }) => boolean;
      };
      if (nav.share && nav.canShare?.({ files: [arquivo] })) {
        try {
          await nav.share({ files: [arquivo], title: 'Orçamento' });
          return;
        } catch (e) {
          // Desistir do seletor nao e' falha — baixar em seguida seria fazer o que
          // ele acabou de recusar.
          if ((e as Error | undefined)?.name === 'AbortError') return;
        }
      }
      baixar(arquivo);
    } finally {
      // O documento pode ter mudado entre um envio e outro: a proxima vez remonta.
      arquivoRef.current = null;
      setOcupado(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        onPointerDown={() => { void preparar(); }}
        onClick={() => { void compartilhar(); }}
        disabled={bloqueio !== null || ocupado}
        style={{
          width: '100%', height: 50, borderRadius: 10, backgroundColor: COBRE, color: '#fff',
          fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center',
          justifyContent: 'center', gap: 8, opacity: bloqueio || ocupado ? 0.5 : 1,
        }}
      >
        {ocupado ? <FileText size={19} /> : <Share2 size={19} />}
        {ocupado ? 'Preparando...' : 'Compartilhar em PDF'}
      </button>
      {/* Botao travado DIZ por que. */}
      {bloqueio && <div style={{ marginTop: 7, fontSize: 13, color: '#b45309' }}>{bloqueio}</div>}
      {erro && <div style={{ marginTop: 7, fontSize: 13, color: '#b91c1c' }}>{erro}</div>}
    </div>
  );
}
